import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTenant } from '@/auth';
import {
  startSessionRecording,
  stopSessionRecording,
  setTenantContext,
  setInterviewContext,
  clearInterviewContext,
  FRESH_ATS_FLAG,
  isAtsPath,
  isPostHogEnabled,
  useSettledFeatureFlag,
} from './posthog';

// Routes where session recording should be enabled
// Pattern captures interview_id for context tracking
const INTERVIEW_ROUTE_PATTERNS = [
  /^\/interview\/([^/]+)\/assessment$/,
];

/**
 * Extracts interview ID from pathname if on an interview route
 * Returns null if not on an interview route
 */
function extractInterviewId(pathname: string): string | null {
  for (const pattern of INTERVIEW_ROUTE_PATTERNS) {
    const match = pathname.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return null;
}

/**
 * SessionReplayController
 *
 * Controls PostHog session recording and interview context based on current route.
 * - Session recording runs on meeting view routes and on /ats pages when the Fresh ATS is served
 * - Interview ID is set as a super property when on interview routes
 *   (allows filtering all events by interview_id in PostHog dashboard)
 * - For all other routes, session recording is disabled but
 *   event capture and other PostHog features remain active
 */
export function SessionReplayController(): null {
  const location = useLocation();
  const { name: tenantName } = useTenant();
  const interviewId = extractInterviewId(location.pathname);
  // Same settled answer AtsSwitch mounts by, so only Fresh ATS sessions are recorded, never Legacy ATS ones.
  const servesFreshAts = useSettledFeatureFlag(FRESH_ATS_FLAG, { skip: !isPostHogEnabled }) === true;
  // Keyed on this boolean, not the path: moving between /ats pages keeps one unbroken recording.
  const isRecordedRoute = interviewId !== null || (servesFreshAts && isAtsPath(location.pathname));

  // Register tenant_name as a super property (sent with all events)
  useEffect(() => {
    if (tenantName) {
      setTenantContext(tenantName);
    }
  }, [tenantName]);

  useEffect(() => {
    if (interviewId) {
      setInterviewContext(interviewId);
    } else {
      clearInterviewContext();
    }
    return () => clearInterviewContext();
  }, [interviewId]);

  useEffect(() => {
    if (!isRecordedRoute) {
      stopSessionRecording();
      return;
    }
    startSessionRecording();
    return () => stopSessionRecording();
  }, [isRecordedRoute]);

  return null;
}
